import React from 'react';

import { Box, Button, Typography } from '@mui/material';
import { ErrorDotIndicator } from '@sharedCR/common/ErrorDotIndicator';
import PendingChangeWrapper from '@sharedCR/common/PendingChangeWrapper';
import { PresenceSectionDot } from '@sharedCR/common/PresenceDot';
import { ScheduleNotSetIcon } from '@sharedCR/coreDesignSystem/icons';
import { LoansPaths } from 'app/configs/routesConfig/loans';
import {
  getShouldDisableFirstPaymentDate,
  getShouldDisableFundingDate,
} from 'app/pages/loanApplication/tabs/loanClosingCDS/shared/ClosingInformation/ClosingInformation.utils';
import { checkIsTabHidden } from 'app/pages/system/autoLockRules/AutoLockRules.utils';
import dayjs from 'dayjs';
import _ from 'lodash';

import { IconSize } from 'constants/coreDesignSystem';
import { COMMON_DATE_FORMAT, COMMON_DATETIME_FORMAT } from 'constants/date';
import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { TRUST_ENTITY_NAMES_NUMBER } from 'constants/loanApplication';
import { MAX_NUMBER_VALUE } from 'constants/number';
import {
  closingInTrustTypeOptions,
  closingLocationOptions,
  fundingInformationTypeOptions,
  stateOptions,
  statesWithNationalCategoryOptions,
} from 'constants/options';
import { PendingChangesActionType } from 'constants/pendingChanges';
import { getConditionalArray, getConditionalObject } from 'utils/data';
import { calculatePendingChanges } from 'utils/pendingChanges';

import WireAmountHelper from './Content/components/WireAmountHelper';
import WireAmountLabel from './Content/components/WireAmountLabel';
import {
  ClosingFundingDatesFormFieldId,
  FundingInformationFieldId,
  SchedulingFieldId,
  TitleHolderFieldId,
  TrustFieldId,
  WireToInformationFieldId,
} from './Content/Content.constants';
import { getTitleCompanyFormConfig } from './EditDrawer.companyConfig';
import { SECTION_HREF_MAP } from './EditDrawer.hrefs';
import { getManufacturedHomeContactsFormConfig } from './EditDrawer.manufacturedHomeContactsConfig';
import {
  fullWidthWrapperStyles,
  GROUP_TITLE_CLASS_NAME,
  navTabCustomStyles,
  navTextContentStyles,
  navTextStyles,
  navTextWrapperStyles,
  pendingChangesCounterStyles,
} from './EditDrawer.styles';
import { EditDrawerTestIds } from './EditDrawer.testIds';

export { SECTION_HREF_MAP } from './EditDrawer.hrefs';

export const SECTION_NAME_MAP = {
  [SECTION_HREF_MAP.SCHEDULING]: 'Scheduling Information',
  [SECTION_HREF_MAP.TITLE_COMPANY]: 'Title/Settlement Agent',
  [SECTION_HREF_MAP.CLOSING_FUNDING_DATES]: 'Closing & Funding Dates',
  [SECTION_HREF_MAP.FUNDING_INFORMATION]: 'Funding Information',
  [SECTION_HREF_MAP.MANUFACTURED_HOME_CONTACTS]: 'Manufactured Home Contacts',
  [SECTION_HREF_MAP.TITLE_HOLDER]: 'Title Holder',
};

export const DEFAULT_SECTION = SECTION_HREF_MAP.SCHEDULING;

const FIRST_CLOSE_AT_MAX_DATE_VALIDATION_MESSAGE = 'First Closing Date must be before Closing Date';

export const getSchedulingFormConfig = ({
  isTwoPartClosing = false,
  isUnsecured = false,
  closeAt,
} = {}) => {
  const hasCloseAt = !!closeAt;
  const showTwoPartClosing = isTwoPartClosing && !isUnsecured;

  return {
    groups: [
      {
        type: FieldType.GROUP,
        key: 'scheduling-info',
        groupTitle: {
          label: 'Scheduling Information',
          icon: <ScheduleNotSetIcon size={IconSize[16]} />,
          className: GROUP_TITLE_CLASS_NAME,
        },
        fields: [
          {
            fieldId: SchedulingFieldId.ANTICIPATED_CLOSING_DATE,
            type: FieldType.DATE,
            nameInModel: SchedulingFieldId.ANTICIPATED_CLOSING_DATE,
            inputProps: {
              label: 'Anticipated Closing Date',
              variant: 'outlined',
              format: COMMON_DATE_FORMAT,
              required: false,
              clearable: true,
              widthInColumns: 2,
            },
          },
          ...getConditionalArray(showTwoPartClosing, () => [
            {
              fieldId: SchedulingFieldId.FIRST_CLOSE_AT,
              type: FieldType.DATE,
              nameInModel: SchedulingFieldId.FIRST_CLOSE_AT,
              inputProps: {
                label: 'First Closing Date and Time',
                variant: 'outlined',
                format: COMMON_DATETIME_FORMAT,
                includeLocalOffset: true,
                withTime: true,
                required: false,
                clearable: true,
                widthInColumns: 2,
                ...getConditionalObject(!!closeAt, () => ({
                  maxDate: dayjs(closeAt).subtract(1, 'minute'),
                  maxDateValidationMessage: FIRST_CLOSE_AT_MAX_DATE_VALIDATION_MESSAGE,
                })),
              },
            },
          ]),
          {
            fieldId: SchedulingFieldId.CLOSE_AT,
            type: FieldType.DATE,
            nameInModel: SchedulingFieldId.CLOSE_AT,
            inputProps: {
              label: showTwoPartClosing ? 'Second Closing Date and Time' : 'Closing Date and Time',
              variant: 'outlined',
              format: COMMON_DATETIME_FORMAT,
              includeLocalOffset: true,
              withTime: true,
              clearable: true,
              widthInColumns: 2,
              required: false,
            },
          },
          ...getConditionalArray(!isUnsecured, [
            {
              fieldId: SchedulingFieldId.LOCATION,
              type: FieldType.SELECT,
              nameInModel: SchedulingFieldId.LOCATION,
              inputProps: {
                label: 'Closing Location',
                variant: 'outlined',
                options: closingLocationOptions,
                widthInColumns: 2,
                required: hasCloseAt,
                disabled: !hasCloseAt,
              },
            },
          ]),
        ],
      },
    ],
  };
};

export const getClosingFundingDatesFormConfig = ({
  hasCloseAt = false,
  hasNoteDate = false,
  isUnsecured = false,
  businessDays,
  isRescissionApplicable = false,
  isChattel = false,
  fundingDate = null,
} = {}) => ({
  groups: [
    {
      type: FieldType.GROUP,
      key: 'closing-funding-dates',
      groupTitle: {
        label: 'Closing & Funding Dates',
        icon: <ScheduleNotSetIcon size={IconSize[16]} />,
        className: GROUP_TITLE_CLASS_NAME,
      },
      fields: [
        {
          fieldId: ClosingFundingDatesFormFieldId.INTEREST_CREDIT_NOTE_DATE,
          type: FieldType.DATE,
          nameInModel: ClosingFundingDatesFormFieldId.INTEREST_CREDIT_NOTE_DATE,
          inputProps: {
            label: 'Note Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            required: false,
            clearable: true,
            disabled: !hasCloseAt,
            widthInColumns: 2,
          },
        },
        ...getConditionalArray(!isUnsecured, [
          {
            fieldId: ClosingFundingDatesFormFieldId.INTEREST_CREDIT_RESCISSION_DATE,
            type: FieldType.DATE,
            nameInModel: ClosingFundingDatesFormFieldId.INTEREST_CREDIT_RESCISSION_DATE,
            inputProps: {
              label: 'Rescission Date',
              variant: 'outlined',
              format: COMMON_DATE_FORMAT,
              disabled: !hasCloseAt || !isRescissionApplicable,
              clearable: true,
              widthInColumns: 2,
            },
          },
        ]),
        {
          fieldId: ClosingFundingDatesFormFieldId.INTEREST_CREDIT_FUNDING_DATE,
          type: FieldType.DATE,
          nameInModel: ClosingFundingDatesFormFieldId.INTEREST_CREDIT_FUNDING_DATE,
          required: hasNoteDate,
          inputProps: {
            label: 'Funding Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            clearable: true,
            disabled: !hasCloseAt,
            required: hasNoteDate,
            shouldDisableDate: getShouldDisableFundingDate(businessDays || {}),
            widthInColumns: 2,
          },
        },
        {
          fieldId: ClosingFundingDatesFormFieldId.INTEREST_CREDIT_DISBURSEMENT_DATE,
          type: FieldType.DATE,
          nameInModel: ClosingFundingDatesFormFieldId.INTEREST_CREDIT_DISBURSEMENT_DATE,
          inputProps: {
            label: 'Disbursement Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            required: false,
            clearable: true,
            disabled: !hasCloseAt,
            widthInColumns: 2,
          },
        },
        {
          fieldId: ClosingFundingDatesFormFieldId.INTEREST_CREDIT_FIRST_PAYMENT_DATE,
          type: FieldType.DATE,
          nameInModel: ClosingFundingDatesFormFieldId.INTEREST_CREDIT_FIRST_PAYMENT_DATE,
          required: hasNoteDate,
          inputProps: {
            label: 'First Payment Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            shouldDisableDate: getShouldDisableFirstPaymentDate({ isChattel, fundingDate }),
            clearable: true,
            disabled: !hasCloseAt,
            required: hasNoteDate,
            widthInColumns: 2,
          },
        },
        {
          fieldId: ClosingFundingDatesFormFieldId.APPROVAL_DATE,
          type: FieldType.DATE,
          nameInModel: ClosingFundingDatesFormFieldId.APPROVAL_DATE,
          inputProps: {
            label: 'Approval Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            clearable: true,
            disabled: true,
            widthInColumns: 2,
          },
        },
        {
          fieldId: ClosingFundingDatesFormFieldId.APPROVAL_EXPIRATION_DATE,
          type: FieldType.DATE,
          nameInModel: ClosingFundingDatesFormFieldId.APPROVAL_EXPIRATION_DATE,
          inputProps: {
            label: 'Approval Expiration Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            required: false,
            clearable: true,
            widthInColumns: 2,
          },
        },
        {
          fieldId: ClosingFundingDatesFormFieldId.MATURITY_DATE,
          type: FieldType.DATE,
          nameInModel: ClosingFundingDatesFormFieldId.MATURITY_DATE,
          inputProps: {
            label: 'Maturity Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            required: false,
            clearable: true,
            disabled: true,
            widthInColumns: 2,
          },
        },
      ],
    },
  ],
});

export const getFundingInformationFormConfig = ({
  warehouseBankOptions,
  feeProviderOptions,
  onFeeProviderChange,
  onOpenFeeProviderLocationTable,
  isChangeLocationButtonDisabled,
  isChangeLocationButtonVisible,
  onChangeAdvanceAmountPercentDependantField,
  isUnsecured = false,
}) => ({
  groups: [
    {
      type: FieldType.GROUP,
      key: 'funding-info',
      groupTitle: { label: 'Funding Information', className: GROUP_TITLE_CLASS_NAME },
      fields: [
        ...getConditionalArray(!isUnsecured, [
          {
            fieldId: FundingInformationFieldId.WAREHOUSE_BANK_ID,
            type: FieldType.SELECT,
            nameInModel: FundingInformationFieldId.WAREHOUSE_BANK_ID,
            inputProps: {
              label: 'Warehouse Bank',
              variant: 'outlined',
              options: warehouseBankOptions,
              widthInColumns: 2,
            },
          },
          {
            fieldId: FundingInformationFieldId.COMMITMENT_NUMBER,
            type: FieldType.INPUT,
            nameInModel: FundingInformationFieldId.COMMITMENT_NUMBER,
            inputProps: {
              type: FieldInputType.TEXT_EXCEPT_SPECIAL_SYMBOLS,
              label: 'Commitment Number',
              variant: 'outlined',
              required: false,
              widthInColumns: 2,
            },
          },
        ]),
        {
          fieldId: FundingInformationFieldId.TYPE,
          type: FieldType.SELECT,
          nameInModel: FundingInformationFieldId.TYPE,
          inputProps: {
            label: 'Type',
            variant: 'outlined',
            options: fundingInformationTypeOptions,
            disabled: true,
            widthInColumns: 2,
          },
        },
        {
          fieldId: FundingInformationFieldId.WIRE_AMOUNT,
          type: FieldType.INPUT,
          nameInModel: FundingInformationFieldId.WIRE_AMOUNT,
          inputProps: {
            type: FieldInputType.CURRENCY,
            label: 'Wire Amount',
            variant: 'outlined',
            required: false,
            widthInColumns: 2,
          },
        },
        {
          fieldId: FundingInformationFieldId.FUNDING_CONFIRMATION_NUMBER,
          type: FieldType.INPUT,
          nameInModel: FundingInformationFieldId.FUNDING_CONFIRMATION_NUMBER,
          inputProps: {
            type: FieldInputType.TEXT,
            label: 'Funding Confirmation Number',
            variant: 'outlined',
            widthInColumns: 2,
          },
        },
        ...getConditionalArray(!isUnsecured, [
          {
            fieldId: FundingInformationFieldId.WAREHOUSE_FUNDING_ORDERED,
            type: FieldType.DATE,
            nameInModel: FundingInformationFieldId.WAREHOUSE_FUNDING_ORDERED,
            inputProps: {
              label: 'Warehouse Funding Ordered',
              variant: 'outlined',
              format: COMMON_DATE_FORMAT,
              clearable: true,
              widthInColumns: 2,
            },
          },
        ]),
        {
          fieldId: FundingInformationFieldId.WIRE_POSTED_DATE,
          type: FieldType.DATE,
          nameInModel: FundingInformationFieldId.WIRE_POSTED_DATE,
          inputProps: {
            label: 'Wire Posted Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            clearable: true,
            widthInColumns: 2,
          },
        },
        ...getConditionalArray(!isUnsecured, [
          {
            fieldId: FundingInformationFieldId.CURTAILMENT,
            type: FieldType.INPUT,
            nameInModel: FundingInformationFieldId.CURTAILMENT,
            inputProps: {
              type: FieldInputType.TEXT_EXCEPT_SPECIAL_SYMBOLS,
              label: 'Curtailment',
              variant: 'outlined',
              required: false,
              widthInColumns: 2,
            },
          },
        ]),
      ],
    },
    {
      type: FieldType.GROUP,
      key: 'wire-comments',
      fields: [
        {
          fieldId: FundingInformationFieldId.WIRE_COMMENTS,
          type: FieldType.INPUT,
          nameInModel: FundingInformationFieldId.WIRE_COMMENTS,
          inputProps: {
            type: FieldInputType.TEXT_EXCEPT_SPECIAL_SYMBOLS,
            label: 'Wire Comment',
            variant: 'outlined',
            required: false,
            multiline: true,
            rows: 3,
            maxLength: 500,
            widthInColumns: 3,
          },
        },
      ],
    },
    ...getConditionalArray(!isUnsecured, [
      {
        type: FieldType.GROUP,
        key: 'wire-to-info',
        groupTitle: { label: 'Wire To Information', className: GROUP_TITLE_CLASS_NAME },
        ...(isChangeLocationButtonVisible && {
          additionalGroupContentConfig: {
            fields: [
              {
                component: () => (
                  <div className="w-full">
                    <Button
                      color="primary"
                      data-testid={EditDrawerTestIds.CHANGE_LOCATION_BUTTON}
                      disabled={isChangeLocationButtonDisabled}
                      type="button"
                      variant="contained"
                      onClick={onOpenFeeProviderLocationTable}
                    >
                      Change location
                    </Button>
                  </div>
                ),
              },
            ],
          },
        }),
        fields: [
          {
            fieldId: WireToInformationFieldId.FEE_PROVIDER_ID,
            type: FieldType.SELECT,
            nameInModel: WireToInformationFieldId.FEE_PROVIDER_ID,
            inputProps: {
              label: 'Fee Providers',
              variant: 'outlined',
              options: feeProviderOptions,
              required: false,
              groupBy: ({ type }) => type,
              withSortedOptions: true,
              customOnChange: onFeeProviderChange,
              widthInColumns: 2,
            },
          },
          {
            fieldId: WireToInformationFieldId.COMPANY_NAME,
            type: FieldType.INPUT,
            nameInModel: WireToInformationFieldId.COMPANY_NAME,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Company Name',
              variant: 'outlined',
              disabled: true,
              widthInColumns: 2,
            },
          },
          {
            fieldId: WireToInformationFieldId.FEE_PROVIDER_ADDRESS,
            type: FieldType.INPUT,
            nameInModel: WireToInformationFieldId.FEE_PROVIDER_ADDRESS,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Company Address',
              variant: 'outlined',
              disabled: true,
              widthInColumns: 2,
            },
          },
          {
            fieldId: WireToInformationFieldId.BANK,
            type: FieldType.INPUT,
            nameInModel: WireToInformationFieldId.BANK,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Bank',
              variant: 'outlined',
              disabled: false,
              widthInColumns: 2,
            },
          },
          {
            fieldId: WireToInformationFieldId.CITY,
            type: FieldType.INPUT,
            nameInModel: WireToInformationFieldId.CITY,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'City',
              variant: 'outlined',
              disabled: false,
              widthInColumns: 2,
            },
          },
          {
            fieldId: WireToInformationFieldId.STATE,
            type: FieldType.SELECT,
            nameInModel: WireToInformationFieldId.STATE,
            inputProps: {
              label: 'State',
              variant: 'outlined',
              disabled: false,
              widthInColumns: 2,
              options: statesWithNationalCategoryOptions,
            },
          },
          {
            fieldId: WireToInformationFieldId.ABA_NUMBER,
            type: FieldType.INPUT,
            nameInModel: WireToInformationFieldId.ABA_NUMBER,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'ABA Number',
              variant: 'outlined',
              disabled: false,
              widthInColumns: 2,
            },
          },
          {
            fieldId: WireToInformationFieldId.ACCOUNT_NUMBER,
            type: FieldType.INPUT,
            nameInModel: WireToInformationFieldId.ACCOUNT_NUMBER,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Account Number',
              variant: 'outlined',
              disabled: false,
              widthInColumns: 2,
            },
          },
          {
            fieldId: WireToInformationFieldId.REFERENCE_NUMBER,
            type: FieldType.INPUT,
            nameInModel: WireToInformationFieldId.REFERENCE_NUMBER,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Reference #',
              variant: 'outlined',
              required: false,
              widthInColumns: 2,
            },
          },
          {
            fieldId: WireToInformationFieldId.CONTACT,
            type: FieldType.INPUT,
            nameInModel: WireToInformationFieldId.CONTACT,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Contact',
              variant: 'outlined',
              widthInColumns: 2,
            },
          },
        ],
      },
      {
        type: FieldType.GROUP,
        key: 'wire-to-warehouse',
        fields: [
          {
            fieldId: WireToInformationFieldId.WAREHOUSE_BANK_ID,
            type: FieldType.SELECT,
            nameInModel: WireToInformationFieldId.WAREHOUSE_BANK_ID,
            inputProps: {
              label: 'Warehouse Bank',
              variant: 'outlined',
              options: warehouseBankOptions,
              widthInColumns: 2,
            },
          },
          {
            fieldId: WireToInformationFieldId.WIRE_NUMBER,
            type: FieldType.INPUT,
            nameInModel: WireToInformationFieldId.WIRE_NUMBER,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Wire Number',
              variant: 'outlined',
              required: false,
              widthInColumns: 2,
            },
          },
          {
            fieldId: WireToInformationFieldId.FED_REFERENCE_NUMBER,
            type: FieldType.INPUT,
            nameInModel: WireToInformationFieldId.FED_REFERENCE_NUMBER,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Fed Reference Number',
              variant: 'outlined',
              required: false,
              maxLength: 50,
              widthInColumns: 2,
            },
          },
          {
            fieldId: WireToInformationFieldId.WIRE_AMOUNT,
            type: FieldType.INPUT,
            nameInModel: WireToInformationFieldId.WIRE_AMOUNT,
            inputProps: {
              type: FieldInputType.CURRENCY,
              label: <WireAmountLabel />,
              variant: 'outlined',
              required: false,
              customOnChange: onChangeAdvanceAmountPercentDependantField,
              widthInColumns: 2,
              customHelper: WireAmountHelper,
            },
          },
          {
            fieldId: WireToInformationFieldId.ADVANCE_AMOUNT_PERCENT,
            type: FieldType.INPUT,
            nameInModel: WireToInformationFieldId.ADVANCE_AMOUNT_PERCENT,
            inputProps: {
              type: FieldInputType.PERCENTAGE,
              label: 'Advance Amount %',
              variant: 'outlined',
              disabled: true,
              decimalScale: 2,
              decimalSeparator: '.',
              fixedDecimalScale: true,
              isNumericString: true,
              maxValue: MAX_NUMBER_VALUE,
              widthInColumns: 2,
            },
          },
          {
            fieldId: WireToInformationFieldId.ADVANCE_AMOUNT,
            type: FieldType.INPUT,
            nameInModel: WireToInformationFieldId.ADVANCE_AMOUNT,
            inputProps: {
              type: FieldInputType.CURRENCY,
              label: 'Advance Amount',
              variant: 'outlined',
              required: false,
              customOnChange: onChangeAdvanceAmountPercentDependantField,
              widthInColumns: 2,
            },
          },
          {
            fieldId: WireToInformationFieldId.ADD_ADVANCE_AMOUNT,
            type: FieldType.INPUT,
            nameInModel: WireToInformationFieldId.ADD_ADVANCE_AMOUNT,
            inputProps: {
              type: FieldInputType.CURRENCY,
              label: 'Add Advance Amount',
              variant: 'outlined',
              required: false,
              customOnChange: onChangeAdvanceAmountPercentDependantField,
              widthInColumns: 2,
            },
          },
        ],
      },
      {
        type: FieldType.GROUP,
        key: 'further-credit-to',
        groupTitle: { label: 'Further Credit To', className: GROUP_TITLE_CLASS_NAME },
        fields: [
          {
            fieldId: WireToInformationFieldId.FURTHER_CREDIT_ABA_NUMBER,
            type: FieldType.INPUT,
            nameInModel: WireToInformationFieldId.FURTHER_CREDIT_ABA_NUMBER,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'ABA Number',
              variant: 'outlined',
              widthInColumns: 2,
            },
          },
          {
            fieldId: WireToInformationFieldId.FURTHER_CREDIT_ACCOUNT_NUMBER,
            type: FieldType.INPUT,
            nameInModel: WireToInformationFieldId.FURTHER_CREDIT_ACCOUNT_NUMBER,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Account Number',
              variant: 'outlined',
              widthInColumns: 2,
            },
          },
          {
            fieldId: WireToInformationFieldId.FURTHER_CREDIT_CONTACT,
            type: FieldType.INPUT,
            nameInModel: WireToInformationFieldId.FURTHER_CREDIT_CONTACT,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Contact',
              variant: 'outlined',
              widthInColumns: 2,
            },
          },
        ],
      },
    ]),
  ],
});

export const getTitleHolderFormConfig = ({
  borrowerVestingOptions = [],
  mannerHeldOptions = [],
  isTrustSectionVisible = false,
  isBplLoanPurposeType = false,
  isMannerHeldDisabled = false,
  isCompanyNameFieldVisible = false,
  isChattel = false,
  titleHoldersSection,
  controlsSection,
  populateVestingButton,
  copyBorrowersButton,
} = {}) => ({
  groups: [
    ...getConditionalArray(!isBplLoanPurposeType, () => [
      {
        type: FieldType.GROUP,
        key: 'title-holders',
        groupTitle: { label: 'Title Holders', className: GROUP_TITLE_CLASS_NAME },
        fields: [],
        additionalGroupContentConfig: {
          wrapperStyles: fullWidthWrapperStyles,
          fields: [
            ...getConditionalArray(!!titleHoldersSection, [
              {
                component: titleHoldersSection,
              },
            ]),
            ...getConditionalArray(!!controlsSection, [
              {
                component: controlsSection,
              },
            ]),
          ],
        },
      },
    ]),
    {
      type: FieldType.GROUP,
      key: 'vesting-info',
      groupTitle: { label: 'Vesting Information', className: GROUP_TITLE_CLASS_NAME },
      fields: [
        {
          fieldId: TitleHolderFieldId.VESTING_DESCRIPTION,
          type: FieldType.INPUT,
          nameInModel: TitleHolderFieldId.VESTING_DESCRIPTION,
          inputProps: {
            label: 'Vesting Description',
            variant: 'outlined',
            multiline: true,
            rows: 10,
            maxLength: Number.MAX_SAFE_INTEGER,
            widthInColumns: 3,
          },
        },
        {
          fieldId: TitleHolderFieldId.VESTING_TYPE_ID,
          type: FieldType.SELECT,
          nameInModel: TitleHolderFieldId.VESTING_TYPE_ID,
          inputProps: {
            label: 'Borrower Vesting',
            variant: 'outlined',
            options: borrowerVestingOptions,
            required: false,
            widthInColumns: 2,
          },
        },
        {
          fieldId: TitleHolderFieldId.MANNER_HELD,
          type: FieldType.SELECT,
          nameInModel: TitleHolderFieldId.MANNER_HELD,
          inputProps: {
            label: 'Title Held As',
            variant: 'outlined',
            options: mannerHeldOptions,
            disableClearable: true,
            required: !isChattel,
            disabled: isMannerHeldDisabled,
            widthInColumns: 2,
          },
        },
        ...getConditionalArray(isBplLoanPurposeType && isCompanyNameFieldVisible, [
          {
            fieldId: TitleHolderFieldId.BPL_COMPANY_NAME,
            type: FieldType.INPUT,
            nameInModel: TitleHolderFieldId.BPL_COMPANY_NAME,
            inputProps: {
              label: 'Company Name',
              variant: 'outlined',
              required: true,
              widthInColumns: 2,
            },
          },
        ]),
      ],
      ...getConditionalObject(!!populateVestingButton, {
        additionalGroupContentConfig: {
          wrapperStyles: fullWidthWrapperStyles,
          fields: [
            {
              component: populateVestingButton,
            },
          ],
        },
      }),
    },
    ...getConditionalArray(isTrustSectionVisible && !isBplLoanPurposeType, () => [
      {
        type: FieldType.GROUP,
        key: 'trust-info',
        groupTitle: { label: 'Trust Information', className: GROUP_TITLE_CLASS_NAME },
        fields: [
          {
            fieldId: TrustFieldId.TYPE,
            type: FieldType.SELECT,
            nameInModel: TrustFieldId.TYPE,
            inputProps: {
              label: 'Closing in Trust Type',
              variant: 'outlined',
              options: closingInTrustTypeOptions,
              required: true,
              widthInColumns: 2,
            },
          },
          {
            fieldId: TrustFieldId.ESTABLISHED_DATE,
            type: FieldType.DATE,
            nameInModel: TrustFieldId.ESTABLISHED_DATE,
            inputProps: {
              label: 'Trust Established Date',
              variant: 'outlined',
              format: COMMON_DATE_FORMAT,
              required: true,
              clearable: true,
              widthInColumns: 2,
            },
          },
          {
            fieldId: TrustFieldId.NAME,
            type: FieldType.INPUT,
            nameInModel: TrustFieldId.NAME,
            inputProps: {
              label: 'Trust Name',
              variant: 'outlined',
              maxLength: 300,
              required: true,
              widthInColumns: 2,
            },
          },
          {
            fieldId: TrustFieldId.ESTABLISHED_STATE,
            type: FieldType.SELECT,
            nameInModel: TrustFieldId.ESTABLISHED_STATE,
            inputProps: {
              label: 'Trust Established State',
              variant: 'outlined',
              options: stateOptions,
              required: true,
              widthInColumns: 2,
            },
          },
        ],
      },
      ...['trustee', 'settlor', 'beneficiary'].map(entityType => {
        const capitalizedEntityType = `${entityType.charAt(0).toUpperCase()}${entityType.slice(1)}`;

        return {
          type: FieldType.GROUP,
          key: `${entityType}-names`,
          groupTitle: {
            label: `${capitalizedEntityType} Names`,
            className: GROUP_TITLE_CLASS_NAME,
          },
          fields: Array.from({ length: TRUST_ENTITY_NAMES_NUMBER }).flatMap((__, index) => {
            const orderNumber = index + 1;
            const nameField = {
              fieldId: `${entityType}${orderNumber}`,
              type: FieldType.INPUT,
              nameInModel: `${entityType}${orderNumber}`,
              inputProps: {
                label: `${capitalizedEntityType} ${orderNumber}`,
                variant: 'outlined',
                maxLength: 300,
                required: false,
                widthInColumns: 3,
              },
            };

            if (entityType !== 'trustee') {
              return [nameField];
            }

            return [
              nameField,
              {
                fieldId: `${entityType}${orderNumber}IsOwner`,
                type: FieldType.CHECKBOX,
                nameInModel: `${entityType}${orderNumber}IsOwner`,
                inputProps: {
                  label: 'Is Owner',
                  wrapperStyles: { alignSelf: 'end' },
                },
              },
              { type: FieldType.EMPTY, inputProps: {} },
            ];
          }),
          ...getConditionalObject(
            ['trustee', 'settlor'].includes(entityType) && !!copyBorrowersButton,
            {
              additionalGroupContentConfig: {
                wrapperStyles: fullWidthWrapperStyles,
                fields: [
                  {
                    component: copyBorrowersButton,
                    'data-group-name': entityType,
                  },
                ],
              },
            },
          ),
        };
      }),
    ]),
  ],
});

export const SECTION_FORM_CONFIG_MAP = {
  [SECTION_HREF_MAP.SCHEDULING]: getSchedulingFormConfig,
  [SECTION_HREF_MAP.TITLE_COMPANY]: getTitleCompanyFormConfig,
  [SECTION_HREF_MAP.CLOSING_FUNDING_DATES]: getClosingFundingDatesFormConfig,
  [SECTION_HREF_MAP.FUNDING_INFORMATION]: getFundingInformationFormConfig,
  [SECTION_HREF_MAP.MANUFACTURED_HOME_CONTACTS]: getManufacturedHomeContactsFormConfig,
  [SECTION_HREF_MAP.TITLE_HOLDER]: getTitleHolderFormConfig,
};

const collectFieldNames = (fields, fieldNames) => {
  fields?.forEach(field => {
    if (field.type === FieldType.GROUP && field.fields) {
      collectFieldNames(field.fields, fieldNames);

      return;
    }

    if (field.nameInModel) {
      fieldNames.push(field.nameInModel);
    }
  });
};

const getFieldNamesFromConfig = config => {
  const configObj = _.isFunction(config)
    ? config({
        attendantCompanyOptions: [],
        titleCompanyOptions: [],
        escrowCompanyOptions: [],
        warehouseBankOptions: [],
        feeProviderOptions: [],
        investorsOptions: [],
        servicingCompaniesOptions: [],
      })
    : config;

  const fieldNames = [];

  configObj?.groups?.forEach(group => {
    if (group.type === FieldType.GROUP && group.fields) {
      collectFieldNames(group.fields, fieldNames);

      return;
    }

    group?.fields?.forEach(field => {
      if (field.nameInModel) {
        fieldNames.push(field.nameInModel);
      }
    });
  });

  return fieldNames;
};

const SECTION_FIELD_NAMES_MAP = {
  [SECTION_HREF_MAP.SCHEDULING]: getFieldNamesFromConfig(
    getSchedulingFormConfig({ isTwoPartClosing: true }),
  ),
  [SECTION_HREF_MAP.TITLE_COMPANY]: getFieldNamesFromConfig(
    getTitleCompanyFormConfig({
      titleCompanyOptions: [],
      escrowCompanyOptions: [],
      sellerAttorneyOptions: [],
      buyerAttorneyOptions: [],
      hasTitleCompany: true,
      hasEscrowCompany: true,
      hasSellerAttorney: true,
      hasBuyerAttorney: true,
    }),
  ),
  [SECTION_HREF_MAP.CLOSING_FUNDING_DATES]: getFieldNamesFromConfig(
    getClosingFundingDatesFormConfig(),
  ),
  [SECTION_HREF_MAP.FUNDING_INFORMATION]: getFieldNamesFromConfig(
    getFundingInformationFormConfig({ warehouseBankOptions: [], feeProviderOptions: [] }),
  ),
  [SECTION_HREF_MAP.MANUFACTURED_HOME_CONTACTS]: getFieldNamesFromConfig(
    getManufacturedHomeContactsFormConfig({
      hasBroker: true,
      hasDealer: true,
      hasInvestor: true,
    }),
  ),
  [SECTION_HREF_MAP.TITLE_HOLDER]: [
    ...getFieldNamesFromConfig(
      getTitleHolderFormConfig({
        isTrustSectionVisible: true,
        isBplLoanPurposeType: false,
      }),
    ),
    ...getFieldNamesFromConfig(
      getTitleHolderFormConfig({
        isTrustSectionVisible: false,
        isBplLoanPurposeType: true,
        isCompanyNameFieldVisible: true,
      }),
    ),
  ],
};

const TRUST_BACKEND_TO_FORM_FIELD_MAP = {
  type: 'trustType',
  name: 'trustName',
  establishedDate: 'trustEstablishedDate',
  establishedState: 'trustEstablishedState',
};

const isDefaultBooleanFromEntityCreation = (key, change) => {
  const isBooleanField = key.endsWith('IsOwner');
  const isNullToFalse =
    _.isNil(change.initialValue) && (change.newValue === false || change.newValue === 'False');

  return isBooleanField && isNullToFalse;
};

export const mapTrustPendingChanges = trustChanges =>
  Object.entries(trustChanges || {}).reduce((acc, [key, value]) => {
    if (isDefaultBooleanFromEntityCreation(key, value)) {
      return acc;
    }

    acc[TRUST_BACKEND_TO_FORM_FIELD_MAP[key] || key] = value;

    return acc;
  }, {});

export const getUnvisitedFieldNames = visitedSections =>
  Object.entries(SECTION_FIELD_NAMES_MAP)
    .filter(([sectionKey]) => !visitedSections.has(sectionKey))
    .flatMap(([, fieldNames]) => fieldNames);

export const getInvalidSections = validationErrors =>
  Object.entries(SECTION_FIELD_NAMES_MAP)
    .filter(([, fieldNames]) => fieldNames.some(name => !_.isNil(_.get(validationErrors, name))))
    .map(([sectionKey]) => sectionKey);

export const calculateSectionPendingChangesCounts = pendingChangesData => {
  const counts = {};
  const data = pendingChangesData || EMPTY_PENDING_DATA;

  Object.entries(SECTION_FIELD_NAMES_MAP).forEach(([sectionKey, fieldNames]) => {
    const fieldNamesSet = new Set(fieldNames);
    const sectionData = Object.entries(data).reduce((acc, [fieldName, fieldValue]) => {
      if (fieldNamesSet.has(fieldName)) {
        acc[fieldName] = fieldValue;
      }

      return acc;
    }, {});
    const { count } = calculatePendingChanges(sectionData);
    counts[sectionKey] = count;
  });

  return counts;
};

const HEAD_TAB_VALUE = 'closing';

export const getHeadTabs = ({ pendingChangesCount, isUnsecured = false }) => [
  {
    value: HEAD_TAB_VALUE,
    label: isUnsecured ? 'Funding' : 'Closing',
    pendingChangesCount,
  },
];

const EMPTY_PENDING_DATA = {};

const UNSECURED_HIDDEN_SECTIONS = new Set([
  SECTION_HREF_MAP.TITLE_COMPANY,
  SECTION_HREF_MAP.TITLE_HOLDER,
]);

// Manufactured home contacts only apply to Chattel loans — everyone else never sees the tab.
const CHATTEL_ONLY_SECTIONS = new Set([SECTION_HREF_MAP.MANUFACTURED_HOME_CONTACTS]);

const isSectionChattelOnly = section => CHATTEL_ONLY_SECTIONS.has(section);

// Direct URL navigation (deep-link) can request a chattel-only subtab (e.g. Manufactured
// Home Contacts) even though the tab is hidden from the nav for a non-chattel loan, or before
// the loan's chattel status has finished loading. Resolve to a section that is guaranteed to be
// in the currently rendered navigation-tab list, so the MUI Tabs `value` never falls outside its
// own tab list (which floods the console with "Tabs value is invalid" warnings) and the
// chattel-only panel never renders for a non-chattel loan. Shared by EditDrawer (nav Tabs value)
// and Content (panel/config rendering) so both stay in sync.
export const resolveActiveSection = ({ section, isChattel = false }) => {
  const candidate = section || DEFAULT_SECTION;

  if (isSectionChattelOnly(candidate) && !isChattel) {
    return DEFAULT_SECTION;
  }

  return candidate;
};

export const getNavigationTabs = ({
  applicationId,
  tab,
  history,
  search,
  pendingChangesCounts,
  invalidSections = [],
  hiddenPageIds,
  hiddenFieldIds,
  isUnsecured = false,
  isChattel = false,
}) =>
  Object.entries(SECTION_NAME_MAP).reduce((acc, [value, tabLabel]) => {
    if (checkIsTabHidden(value, hiddenPageIds, hiddenFieldIds)) {
      return acc;
    }

    if (isUnsecured && UNSECURED_HIDDEN_SECTIONS.has(value)) {
      return acc;
    }

    if (!isChattel && isSectionChattelOnly(value)) {
      return acc;
    }

    const pendingChangesCount = pendingChangesCounts?.[value] || 0;

    acc.push({
      value,
      label: (
        <Box sx={navTextWrapperStyles}>
          {!!pendingChangesCount && (
            <PendingChangeWrapper
              isSmall
              className="pending-changes-count-wrapper"
              pendingChangesActionType={PendingChangesActionType.MODIFIED}
              sx={pendingChangesCounterStyles}
              textSelector="span"
            >
              <Typography className="pending-changes-count" variant="regular12">
                {pendingChangesCount}
              </Typography>
            </PendingChangeWrapper>
          )}

          <Box sx={navTextContentStyles}>
            <PresenceSectionDot sectionId={value} />
            <Typography
              data-truncated
              className="nav-tab-label truncate"
              sx={navTextStyles}
              variant="regular12"
            >
              {tabLabel}
            </Typography>

            {invalidSections.includes(value) && <ErrorDotIndicator />}
          </Box>
        </Box>
      ),
      customStyles: navTabCustomStyles,
      onClick: () => {
        const path = LoansPaths.applicationManagement.generatePath({
          applicationId,
          tab,
          subtab: value,
        });
        history.push({ pathname: path, search });
      },
    });

    return acc;
  }, []);
